/**
 * Rozpoznání video zdroje.
 *
 * Tohle je švy, na kterém stojí FÁZE 2 (reupload z YouTube/Vimeo na vlastní HLS):
 * až se videa přesunou, změní se JEN tenhle soubor a URL v datech.
 * Přehrávač, otázky ani bodování se nedotknou.
 */

export type VideoSource =
  | { provider: 'youtube'; id: string }
  | { provider: 'vimeo'; id: string }
  | { provider: 'hls'; url: string }
  | { provider: 'file'; url: string }

/** `https://youtu.be/XXXXXXXXXXX` i `watch?v=` a `/embed/`. */
const YOUTUBE = [
  /(?:youtu\.be\/)([\w-]{11})/,
  /(?:youtube\.com\/watch\?(?:.*&)?v=)([\w-]{11})/,
  /(?:youtube(?:-nocookie)?\.com\/embed\/)([\w-]{11})/,
]

const VIMEO = /vimeo\.com\/(?:video\/)?(\d+)/

export function parseVideoSource(url: string): VideoSource | null {
  const trimmed = url.trim()
  if (trimmed === '') return null

  for (const pattern of YOUTUBE) {
    const match = pattern.exec(trimmed)
    if (match?.[1]) return { provider: 'youtube', id: match[1] }
  }

  const vimeo = VIMEO.exec(trimmed)
  if (vimeo?.[1]) return { provider: 'vimeo', id: vimeo[1] }

  if (/\.m3u8(\?|$)/i.test(trimmed)) return { provider: 'hls', url: trimmed }
  if (/\.(mp4|webm|ogv)(\?|$)/i.test(trimmed)) return { provider: 'file', url: trimmed }

  return null
}

export interface EmbedOptions {
  /**
   * Počáteční ztlumení — zapisuje se do adresy, takže se NESMÍ měnit
   * za běhu. Změna adresy znovu načte iframe a video by začalo od začátku.
   * Hlasitost se dál řídí výhradně přes `postMessage`.
   */
  muted: boolean
  controls: boolean
  /** Origin pro YouTube API; v testech nedostupný. */
  origin?: string
}

/**
 * Sestaví URL iframu.
 *
 * YouTube jde přes `youtube-nocookie.com` — v placeném kurzu nemá co dělat
 * sledovací cookie nastavená bez souhlasu uživatele.
 *
 * VLASTNÍ OVLÁDÁNÍ POSKYTOVATELE JE VŽDY VYPNUTÉ.
 *
 * Aplikace má vlastní pauzu, zvuk i celou obrazovku, takže chrome přehrávače
 * jen zdvojoval funkce a mátl: dvě ozubená kolečka, dva indikátory načítání,
 * název videa, tlačítko pro zkopírování odkazu a logo YouTube uprostřed
 * placeného školení.
 *
 * `options.controls` se proto u vložených přehrávačů ignoruje. Platí jen
 * pro nativní `<video>` (HLS ve fázi 2).
 */
export function buildEmbedUrl(source: VideoSource, options: EmbedOptions): string {
  switch (source.provider) {
    case 'youtube': {
      const params = new URLSearchParams({
        autoplay: '1',
        mute: options.muted ? '1' : '0',
        // Skryje spodní lištu, ozubené kolo, název i logo YouTube
        controls: '0',
        // Bez vlastního tlačítka celé obrazovky — máme svoje
        fs: '0',
        playsinline: '1',
        rel: '0',
        modestbranding: '1',
        iv_load_policy: '3',
        enablejsapi: '1',
        // Musí odpovídat `id` v handshaku, jinak přehrávač neposílá stavy
        widgetid: '1',
      })
      if (options.origin) params.set('origin', options.origin)
      return `https://www.youtube-nocookie.com/embed/${source.id}?${params.toString()}`
    }

    case 'vimeo': {
      const params = new URLSearchParams({
        autoplay: '1',
        muted: options.muted ? '1' : '0',
        controls: '0',
        playsinline: '1',
        title: '0',
        byline: '0',
        portrait: '0',
        dnt: '1',
      })
      return `https://player.vimeo.com/video/${source.id}?${params.toString()}`
    }

    case 'hls':
    case 'file':
      return source.url
  }
}

/** Přehrává se přes <video>, ne přes iframe? */
export function isNativeSource(source: VideoSource): boolean {
  return source.provider === 'hls' || source.provider === 'file'
}
